import React, { useEffect, useState } from 'react';
import { auth, db } from './firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { el } from './i18n/el';

export default function Profile() {
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const user = auth.currentUser;

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (isMounted) {
          setDisplayName(userDoc.exists() ? (userDoc.data().displayName || user.displayName || '') : (user.displayName || ''));
        }
      } catch (e) {
        setDisplayName(user?.displayName || '');
      }
    };
    load();
    return () => { isMounted = false; };
  }, [user]);

  const showToast = (type, message) => {
    setToast({ visible: true, type, message });
    setTimeout(() => setToast({ visible: false, type: 'success', message: '' }), 2500);
  };

  const handleSave = async () => {
    if (!user) return;
    if (!displayName.trim()) {
      showToast('error', el.profileNameRequired);
      return;
    }
    try {
      setSaving(true);
      await updateProfile(user, { displayName: displayName.trim() });
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, { displayName: displayName.trim(), email: user.email || '' }, { merge: true });

      // Also persist this member's display name in company membership
      try {
        // Determine companyId: owner -> uid; member -> from userMemberships
        let companyId = user.uid;
        const membershipRef = doc(db, 'userMemberships', user.uid);
        const membershipSnap = await getDoc(membershipRef);
        if (membershipSnap.exists()) {
          const data = membershipSnap.data();
          if (data?.companyId) companyId = data.companyId;
        }
        const memberRef = doc(db, 'companies', companyId, 'members', user.uid);
        await setDoc(memberRef, { name: displayName.trim(), email: user.email || '' }, { merge: true });
      } catch (e) {
        console.warn('Could not update company membership record:', e);
      }
      showToast('success', el.profileUpdated);
    } catch (e) {
      console.error('Profile update failed:', e);
      showToast('error', el.profileUpdateFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const user = auth.currentUser;
    if (!user) return;
    if (!currentPassword || !newPassword || !confirmPassword) {
      showToast('error', el.profileFillPasswordFields);
      return;
    }
    if (newPassword.length < 8) {
      showToast('error', el.profilePasswordShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('error', el.profilePasswordMismatch);
      return;
    }
    try {
      setPasswordSaving(true);
      const cred = EmailAuthProvider.credential(user.email || '', currentPassword);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('success', el.profilePasswordUpdated);
    } catch (e) {
      console.error('Password update failed:', e);
      const msg = e?.code === 'auth/wrong-password' ? el.profileWrongPassword : el.profilePasswordChangeFailed;
      showToast('error', msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center">{el.profileSignInPrompt}</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-6">
      {toast.visible && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>{toast.message}</div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-blue-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">{el.profileTitle}</h2>
          <p className="text-blue-100 text-sm">{el.profileSubtitle}</p>
        </div>

        <div className="p-6 space-y-8">
          {/* Personal Info */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{el.profilePersonal}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{el.profileDisplayName}</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={el.profileNamePlaceholder} />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{el.email}</label>
                <input type="email" className="w-full border rounded px-3 py-2 bg-gray-100" value={user.email || ''} disabled />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {saving ? el.profileSaving : el.profileSaveChanges}
              </button>
            </div>
          </section>

          {/* Security */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{el.profileSecurity}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{el.profileCurrentPassword}</label>
                <input type="password" className="w-full border rounded px-3 py-2" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" placeholder="••••••••" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{el.profileNewPassword}</label>
                <input type="password" className="w-full border rounded px-3 py-2" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" placeholder={el.profileNewPasswordPlaceholder} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{el.confirmPassword}</label>
                <input type="password" className="w-full border rounded px-3 py-2" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" placeholder={el.profileConfirmPasswordPlaceholder} />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={handleChangePassword} disabled={passwordSaving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {passwordSaving ? el.profileUpdatingPassword : el.profileUpdatePassword}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}


